import { Service } from '@/services/api';
import { ReviewEditInput } from '@/types/ai';
import { ConfirmBinderResponse, ExternalPolicyBinderSubmission } from '@/types/ExternalPolicyBinder';

class ExternalPolicyBinderService extends Service {
  // The prompt version decides which fields come out of the binder, so the submitter
  // names it rather than the server picking whichever was published last.
  async submitBinder(
    accountId: string | number,
    file: File,
    promptVersionId: number,
  ): Promise<ExternalPolicyBinderSubmission> {
    return await this.postFile<ExternalPolicyBinderSubmission>(
      `/v2/accounts/${accountId}/external-policy-binders`,
      file,
      { promptVersionId: String(promptVersionId) },
    ).then(this.handleResponse);
  }

  async listSubmissions(accountId: string | number, activeOnly = false): Promise<ExternalPolicyBinderSubmission[]> {
    return await this.get<ExternalPolicyBinderSubmission[]>(
      `/v2/accounts/${accountId}/external-policy-binders`,
      activeOnly ? { active: 'true' } : undefined,
    ).then(this.handleResponse);
  }

  async getSubmission(accountId: string | number, submissionId: number): Promise<ExternalPolicyBinderSubmission> {
    return await this.get<ExternalPolicyBinderSubmission>(
      `/v2/accounts/${accountId}/external-policy-binders/${submissionId}`,
    ).then(this.handleResponse);
  }

  // The policy records are built server-side from the extraction; only the reviewer's
  // in-flight edits travel with the confirm. Edits already saved on the review screen are
  // folded in by the server, so an untouched confirm sends none.
  async confirmSubmission(
    accountId: string | number,
    submissionId: number,
    edits: ReviewEditInput[] = [],
  ): Promise<ConfirmBinderResponse> {
    return await this.post<ConfirmBinderResponse>(
      `/v2/accounts/${accountId}/external-policy-binders/${submissionId}/confirm`,
      { edits },
    ).then(this.handleResponse);
  }

  async discardSubmission(accountId: string | number, submissionId: number, reason?: string): Promise<void> {
    await this.post<void>(`/v2/accounts/${accountId}/external-policy-binders/${submissionId}/discard`, {
      reason: reason ?? '',
    }).then(this.handleResponse);
  }
}

export const externalPolicyBinderService = new ExternalPolicyBinderService();
